import React, { useEffect, useRef, useState } from "react";
import { Tooltip, useTheme } from "@mui/material";
import { FiCheck, FiEdit2, FiX } from "react-icons/fi";
import {
  fetchMembers,
  MEMBER_STATUS,
  MEMBER_TYPE,
  orgErrorMessage,
  readSession,
  updateOrganisationAsAdmin,
} from "../../utils/organisationApi";

const iconButton =
  "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[#4b5565] transition-colors hover:bg-black/[0.07] hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-black disabled:cursor-not-allowed disabled:opacity-40";

// A small icon button with a tooltip. The wrapper lets the tooltip show on a disabled button too.
const TipButton = ({ label, icon: Icon, onClick, disabled, tone = "", ...rest }) => (
  <Tooltip title={label} arrow>
    <span className="inline-flex">
      <button type="button" className={`${iconButton} ${tone}`} onClick={onClick} disabled={disabled} aria-label={label} {...rest}>
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </span>
  </Tooltip>
);

// One fact about the organisation: icon, label, value. `action` sits at the right (an edit button);
// `editor` replaces the value in place, in the same tile, so editing never changes the layout.
export const Fact = ({ icon: Icon, label, children, sub, action, editor }) => (
  <div className="flex min-w-0 items-center gap-2.5 rounded-lg bg-[#f3f6fb] px-3 py-2">
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-accent-blue" aria-hidden="true">
      <Icon className="h-4 w-4" />
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-[11px] leading-none text-accent-gray-light">{label}</p>
      {editor || (
        <>
          <p className="mt-1 truncate text-sm font-semibold leading-tight">{children}</p>
          {sub && <p className="truncate text-[11px] leading-tight text-accent-gray-light">{sub}</p>}
        </>
      )}
    </div>
    {!editor && action}
  </div>
);

// The organisation's name in the modal header, with an edit button beside it. Only the name changes here.
// `badges` are shown after the name and hidden while editing.
export const OrgNameEditor = ({ organisation, disabled, onSaved, onUpdated, badges }) => {
  const theme = useTheme();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(organisation.name || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    setEditing(false);
    setError("");
    setDraft(organisation.name || "");
  }, [organisation.id, organisation.name]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const save = async () => {
    const name = draft.trim();
    if (!name) return setError("Name is required.");
    if (name === organisation.name) return setEditing(false);
    setBusy(true);
    setError("");
    try {
      await updateOrganisationAsAdmin(organisation.id, { name });
      onSaved(`${name} was updated.`);
      await onUpdated();
      setEditing(false);
    } catch (err) {
      setError(orgErrorMessage(err, "Could not rename the organisation."));
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <span className="flex min-w-0 flex-wrap items-center gap-1.5">
        <span className="truncate text-lg">{organisation.name}</span>
        <TipButton label="Edit name" icon={FiEdit2} onClick={() => setEditing(true)} disabled={disabled} />
        {badges}
      </span>
    );
  }

  return (
    <span className="flex min-w-0 flex-col">
      <span className="flex items-center gap-1.5">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              save();
            }
          }}
          maxLength={120}
          aria-label="Organisation name"
          aria-invalid={Boolean(error)}
          disabled={busy}
          className="h-8 min-w-0 flex-1 rounded-md border px-2 text-base font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
          style={{ borderColor: theme.palette.secondary.v4, maxWidth: "22rem" }}
        />
        <TipButton label="Save name" icon={FiCheck} onClick={save} disabled={busy} tone="!bg-accent-blue !text-white hover:!bg-[#1D4690]" />
        <TipButton label="Cancel" icon={FiX} onClick={() => setEditing(false)} disabled={busy} />
      </span>
      {error && (
        <span role="alert" className="text-xs font-normal text-accent-red">
          {error}
        </span>
      )}
    </span>
  );
};

// The Owner tile, with an edit button. Only the owner changes here. The new owner is typed as an email: inside
// the organisation it is matched to a member, from outside it is sent as is for the server to match.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const OwnerFact = ({ icon, organisation, created, disabled, onSaved, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const inside = readSession().organisationId === organisation.id;

  useEffect(() => {
    setEditing(false);
    setError("");
  }, [organisation.id]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const start = () => {
    setEditing(true);
    setValue(organisation.ownerEmail || "");
    setError("");
  };

  const email = value.trim().toLowerCase();
  const unchanged = email === (organisation.ownerEmail || "").toLowerCase();

  const save = async () => {
    if (!EMAIL_PATTERN.test(email)) return setError("Enter a valid email address.");
    if (unchanged) return setEditing(false);
    setBusy(true);
    setError("");
    try {
      if (inside) {
        const { members } = await fetchMembers({ status: MEMBER_STATUS.ACTIVE });
        const member = members.find((item) => item.type === MEMBER_TYPE.MEMBER && item.userId && (item.email || "").toLowerCase() === email);
        if (!member) {
          setError("No member of this organisation has that email.");
          return;
        }
        await updateOrganisationAsAdmin(organisation.id, { ownerUserId: member.userId });
      } else {
        await updateOrganisationAsAdmin(organisation.id, { ownerEmail: email });
      }
      onSaved(`The owner of ${organisation.name} was changed.`);
      await onUpdated();
      setEditing(false);
    } catch (err) {
      setError(orgErrorMessage(err, "Could not change the owner."));
    } finally {
      setBusy(false);
    }
  };

  const action = <TipButton label="Change owner" icon={FiEdit2} onClick={start} disabled={disabled} />;

  const editor = editing ? (
    <>
      <span className="mt-1 flex items-center gap-1.5">
        <input
          ref={inputRef}
          type="email"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              save();
            }
            if (e.key === "Escape") setEditing(false);
          }}
          placeholder="New owner's email"
          aria-label="New owner's email"
          aria-invalid={Boolean(error)}
          disabled={busy}
          className="h-8 min-w-0 flex-1 rounded-md border bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          style={{ borderColor: "#d0d7e2" }}
        />
        <TipButton label="Save owner" icon={FiCheck} onClick={save} disabled={busy || !email || unchanged} tone="!bg-accent-blue !text-white hover:!bg-[#1D4690]" />
        <TipButton label="Cancel" icon={FiX} onClick={() => setEditing(false)} disabled={busy} />
      </span>
      {error && (
        <span role="alert" className="block truncate text-[11px] leading-tight text-accent-red">
          {error}
        </span>
      )}
    </>
  ) : null;

  return (
    <Fact icon={icon} label="Owner" sub={[organisation.ownerEmail, created].filter(Boolean).join(" · ") || undefined} action={action} editor={editor}>
      {organisation.ownerName || "—"}
    </Fact>
  );
};
